import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import IntegrationsModal from '../components/IntegrationsModal';
// ← Style constants 
const inputStyle = { 
  width: "100%", 
  boxSizing: "border-box", 
  background: "rgba(255,255,255,0.05)", 
  border: "1.5px solid rgba(56,189,248,0.25)", 
  borderRadius: 10, 
  padding: "10px 14px", 
  color: "#e2e8f0", 
  fontSize: 15, 
  outline: "none", 
  fontFamily: "inherit" 
};

const labelStyle = { 
  display: "block", 
  color: "#e2e8f0", 
  fontSize: 12.5, 
  letterSpacing: 1.2, 
  textTransform: "uppercase", 
  marginBottom: 6, 
  fontWeight: 600 
};

const sectionStyle = { 
  borderBottom: "1px solid rgba(56,189,248,0.1)", 
  paddingBottom: 16, 
  marginBottom: 16 
};
// moving the tenant form outside the page to avoid reload on every keystroke 
 const TenantForm = ({form, setForm, users = [], accounts = [], mpesa = { own: [], platform: [] }, processing, showEdit, onSubmit, onCancel, submitLabel }) => (
    <div style={{ maxHeight: "80vh", overflowY: "auto", paddingRight: 8 }}>

      {/* Basic info */}
      <div style={sectionStyle}>
        <div style={{ color: "#38bdf8", fontSize: 13.5, fontWeight: 700, marginBottom: 12, letterSpacing: 1 }}>BASIC INFORMATION</div>
        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Tenant Name</label>
          <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Nairobi Water" style={inputStyle} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Subdomain</label>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input value={form.subdomain} onChange={e => setForm(f => ({ ...f, subdomain: e.target.value.toLowerCase() }))} placeholder="e.g. nairobi" style={{ ...inputStyle, flex: 1 }} disabled={!!showEdit} />
            <span style={{ color: "#cbd5e1", fontSize: 14.5, whiteSpace: "nowrap" }}>.smartwater.co.ke</span>
          </div>
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Address</label>
          <input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} placeholder="e.g. Nairobi" style={inputStyle} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Commission Rate (%)</label>
          <input type="number" value={form.commission_rate} onChange={e => setForm(f => ({ ...f, commission_rate: e.target.value }))} placeholder="8.00" style={inputStyle} />
        </div>
      </div>

      {/* Users */}
      <div style={sectionStyle}>
        <div style={{ color: "#a78bfa", fontSize: 13.5, fontWeight: 700, marginBottom: 6, letterSpacing: 1 }}>USERS *</div>
        <div style={{ color: "#cbd5e1", fontSize: 13.5, marginBottom: 10 }}>
          At least one user must be able to sign in on this tenant's site. A user can be assigned to several tenants.
        </div>
        <div style={{ maxHeight: 150, overflowY: "auto", border: "1px solid rgba(56,189,248,0.15)", borderRadius: 10, padding: 6, marginBottom: 10 }}>
          {users.filter(u => u.role !== 'admin').length === 0 && (
            <div style={{ color: "#cbd5e1", fontSize: 14.5, padding: 8 }}>No regular users yet — create one below.</div>
          )}
          {users.filter(u => u.role !== 'admin').map(u => (
            <label key={u.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 8px", color: "#e2e8f0", fontSize: 14.5, cursor: "pointer" }}>
              <input type="checkbox" checked={(form.user_ids || []).includes(u.id)}
                onChange={() => setForm(f => {
                  const ids = f.user_ids || [];
                  return { ...f, user_ids: ids.includes(u.id) ? ids.filter(x => x !== u.id) : [...ids, u.id] };
                })} />
              {u.name} <span style={{ color: "#cbd5e1" }}>{u.email}</span>
            </label>
          ))}
        </div>
        {!showEdit && (
          <>
            <label style={{ display: "flex", alignItems: "center", gap: 10, color: "#e2e8f0", fontSize: 14.5, marginBottom: 10, cursor: "pointer" }}>
              <input type="checkbox" checked={!!form.add_new_user} onChange={e => setForm(f => ({ ...f, add_new_user: e.target.checked }))} />
              Create a new user for this tenant
            </label>
            {form.add_new_user && (
              <div style={{ display: "grid", gap: 10 }}>
                {[
                  { key: 'name', placeholder: 'Full name', type: 'text' },
                  { key: 'email', placeholder: 'Email', type: 'email' },
                  { key: 'password', placeholder: 'Password (min 8 characters)', type: 'password' },
                ].map(fld => (
                  <input key={fld.key} type={fld.type} autoComplete="new-password" placeholder={fld.placeholder}
                    value={form.new_user?.[fld.key] || ''}
                    onChange={e => setForm(f => ({ ...f, new_user: { ...(f.new_user || {}), [fld.key]: e.target.value } }))}
                    style={inputStyle} />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Accounts on this site */}
      <div style={sectionStyle}>
        <div style={{ color: "#7cc4ff", fontSize: 13.5, fontWeight: 700, marginBottom: 6, letterSpacing: 1 }}>ACCOUNTS ON THIS SITE</div>
        <div style={{ color: "#cbd5e1", fontSize: 13.5, marginBottom: 10 }}>
          Meters on these accounts can only be paid for on this site, into this tenant's paybill. An account belongs to one site, so ticking one here moves it from any other.
        </div>
        <div style={{ maxHeight: 170, overflowY: "auto", border: "1px solid rgba(56,189,248,0.15)", borderRadius: 10, padding: 6 }}>
          {accounts.length === 0 && <div style={{ color: "#cbd5e1", fontSize: 14.5, padding: 8 }}>No accounts yet — create them on the Accounts page.</div>}
          {accounts.map(a => {
            const elsewhere = a.tenant_id && (!showEdit || a.tenant_id !== showEdit.id);
            return (
              <label key={a.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 8px", color: "#e2e8f0", fontSize: 14.5, cursor: "pointer" }}>
                <input type="checkbox" checked={(form.account_ids || []).includes(a.id)}
                  onChange={() => setForm(f => {
                    const ids = f.account_ids || [];
                    return { ...f, account_ids: ids.includes(a.id) ? ids.filter(x => x !== a.id) : [...ids, a.id] };
                  })} />
                <span>{a.name} <span style={{ color: "#cbd5e1" }}>· {a.meter_count} meter{Number(a.meter_count) === 1 ? '' : 's'}</span></span>
                {elsewhere && <span style={{ color: "#fbbf24", fontSize: 13 }}>now on {a.tenant_name}</span>}
              </label>
            );
          })}
        </div>
      </div>

      {/* M-Pesa */}
      <div style={sectionStyle}>
        <div style={{ color: "#00a838", fontSize: 13.5, fontWeight: 700, marginBottom: 12, letterSpacing: 1 }}>M-PESA SETTINGS</div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <input type="checkbox" checked={form.mpesa_enabled} onChange={e => setForm(f => ({ ...f, mpesa_enabled: e.target.checked }))} id="mpesa_enabled" />
          <label htmlFor="mpesa_enabled" style={{ color: "#e2e8f0", fontSize: 14.5 }}>Enable M-Pesa payments</label>
        </div>
        {form.mpesa_enabled && (() => {
          const fmt = (it) => `${it.label} — ${it.shortcode_type === 'till' ? `Till ${it.till_number || it.shortcode}` : `Paybill ${it.shortcode}`}${it.environment === 'sandbox' ? ' (sandbox)' : ''}`;
          const chosen = String(form.mpesa_integration_id || 'platform');
          const fromPlatform = mpesa.platform.find((it) => String(it.id) === chosen);
          return (
            <>
              <label htmlFor="mpesa_choice" style={labelStyle}>Paybill / till customers pay into on this site</label>
              <select id="mpesa_choice" value={chosen} onChange={e => setForm(f => ({ ...f, mpesa_integration_id: e.target.value }))} style={inputStyle}>
                <option value="platform">Shared Majic Water paybill (platform)</option>
                {mpesa.own.length > 0 && (
                  <optgroup label="This tenant's own">
                    {mpesa.own.map((it) => <option key={it.id} value={it.id}>{fmt(it)}</option>)}
                  </optgroup>
                )}
                {mpesa.platform.length > 0 && (
                  <optgroup label="Move here from Platform payments">
                    {mpesa.platform.map((it) => <option key={it.id} value={it.id}>{fmt(it)}{it.is_default ? ' — platform main paybill' : ''}</option>)}
                  </optgroup>
                )}
              </select>
              <div style={{ color: "#cbd5e1", fontSize: 13.5, lineHeight: 1.5, marginTop: 8 }}>
                {chosen === 'platform'
                  ? 'Customers on this site pay into the shared Majic Water paybill.'
                  : fromPlatform
                    ? <>Saving moves <b style={{ color: "#ffffff" }}>{fromPlatform.label}</b> from Platform payments to this tenant. Its Safaricom URLs stay the same, so there is nothing to register again.
                        {fromPlatform.is_default && <span style={{ color: "#fbbf24" }}> This is the platform's main paybill — the main site will switch to its next platform paybill.</span>}</>
                    : 'Customers on this site pay into this paybill.'}
                {' '}To add a brand-new paybill for this tenant, use the <b style={{ color: "#38bdf8" }}>Payments</b> button on its card.
              </div>
            </>
          );
        })()}
      </div>

      {/* KCB */}
      <div style={sectionStyle}>
        <div style={{ color: "#3b82f6", fontSize: 13.5, fontWeight: 700, marginBottom: 12, letterSpacing: 1 }}>KCB SETTINGS</div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <input type="checkbox" checked={form.kcb_enabled} onChange={e => setForm(f => ({ ...f, kcb_enabled: e.target.checked }))} id="kcb_enabled" />
          <label htmlFor="kcb_enabled" style={{ color: "#e2e8f0", fontSize: 14.5 }}>Enable KCB payments</label>
        </div>
        {form.kcb_enabled && (
          <div style={{ color: "#cbd5e1", fontSize: 13.5 }}>KCB credentials are managed under Payments.</div>
        )}
      </div>

      {/* Airtel */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ color: "#ef4444", fontSize: 13.5, fontWeight: 700, marginBottom: 12, letterSpacing: 1 }}>AIRTEL MONEY SETTINGS</div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <input type="checkbox" checked={form.airtel_enabled} onChange={e => setForm(f => ({ ...f, airtel_enabled: e.target.checked }))} id="airtel_enabled" />
          <label htmlFor="airtel_enabled" style={{ color: "#e2e8f0", fontSize: 14.5 }}>Enable Airtel Money payments</label>
        </div>
        {form.airtel_enabled && (
          <div style={{ color: "#cbd5e1", fontSize: 13.5 }}>Airtel Money credentials are managed under Payments.</div>
        )}
      </div>

      {/* Buttons */}
      <div style={{ display: "flex", gap: 12 }}>
        <button onClick={onCancel} style={{ flex: 1, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, padding: "12px", color: "#e2e8f0", fontSize: 15, fontWeight: 600, cursor: "pointer" }}>
          Cancel
        </button>
        <button onClick={onSubmit} disabled={processing} style={{ flex: 1, background: "linear-gradient(135deg, #0891b2, #0e7490)", border: "none", borderRadius: 10, padding: "12px", color: "#fff", fontSize: 15, fontWeight: 600, cursor: "pointer" }}>
          {processing ? 'Saving...' : submitLabel}
        </button>
      </div>
    </div>
  );
//page starts here 
export default function TenantsPage() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [users, setUsers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [mpesaOptions, setMpesaOptions] = useState({ own: [], platform: [] });
  const mpesaOnly = (list) => (list || []).filter((it) => it.provider === 'mpesa' && it.active);
  const loadMpesaOptions = async (tenantId) => {
    try {
      const [p, o] = await Promise.all([
        axios.get('/api/integrations', { headers, params: { tenant_id: 'platform' } }),
        tenantId ? axios.get('/api/integrations', { headers, params: { tenant_id: tenantId } }) : Promise.resolve({ data: { integrations: [] } }),
      ]);
      setMpesaOptions({ own: mpesaOnly(o.data.integrations), platform: mpesaOnly(p.data.integrations) });
    } catch { setMpesaOptions({ own: [], platform: [] }); }
  };
  const [integrationsFor, setIntegrationsFor] = useState(undefined); // undefined = closed, null = platform, tenant object
  const [form, setForm] = useState({
    name: '', subdomain: '', address: '',
    use_primary_mpesa: true,
    mpesa_enabled: true, mpesa_shortcode: '',
    mpesa_consumer_key: '', mpesa_consumer_secret: '',
    mpesa_passkey: '',
    kcb_enabled: false, kcb_paybill: '', kcb_api_key: '',
    airtel_enabled: false, airtel_merchant_id: '', airtel_api_key: '',
    commission_rate: 8.00,
    user_ids: [], account_ids: [], mpesa_integration_id: 'platform', add_new_user: false, new_user: { name: '', email: '', password: '' }
  });

  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchTenants = async () => {
    try {
      const [res, usersRes, accountsRes] = await Promise.all([
        axios.get('/api/tenants', { headers }),
        axios.get('/api/users', { headers }),
        axios.get('/api/accounts', { headers }),
      ]);
      setTenants(res.data.tenants);
      setUsers(usersRes.data.users);
      setAccounts(accountsRes.data.accounts || []);
      setLoading(false);
    } catch (err) {
      if (err.response?.status === 401) { localStorage.clear(); navigate('/login'); }
      setError('Failed to load tenants');
      setLoading(false);
    }
  };

  useEffect(() => { fetchTenants(); }, []);

  const resetForm = () => setForm({
    name: '', subdomain: '', address: '',
    use_primary_mpesa: true,
    mpesa_enabled: true, mpesa_shortcode: '',
    mpesa_consumer_key: '', mpesa_consumer_secret: '',
    mpesa_passkey: '',
    kcb_enabled: false, kcb_paybill: '', kcb_api_key: '',
    airtel_enabled: false, airtel_merchant_id: '', airtel_api_key: '',
    commission_rate: 8.00,
    user_ids: [], account_ids: [], mpesa_integration_id: 'platform', add_new_user: false, new_user: { name: '', email: '', password: '' }
  });

  const handleCreate = async () => {
    if (!form.name || !form.subdomain) return;
    if (!form.user_ids.length && !form.add_new_user) {
      setError('Assign at least one user to the tenant');
      return;
    }
    setProcessing(true);
    try {
      const { add_new_user, new_user, ...rest } = form;
      await axios.post('/api/tenants', { ...rest, use_primary_mpesa: String(rest.mpesa_integration_id || 'platform') === 'platform', ...(add_new_user ? { new_user } : {}) }, { headers });
      setShowCreate(false);
      resetForm();
      fetchTenants();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create tenant');
    } finally { setProcessing(false); }
  };

  const handleUpdate = async () => {
    setProcessing(true);
    try {
      if (!form.user_ids?.length) {
        setError('A tenant must have at least one user');
        setProcessing(false);
        return;
      }
      const { users: _u, accounts: _a, mpesa_integration: _m, add_new_user, new_user, ...rest } = form;
      await axios.put(`/api/tenants/${showEdit.id}`, { ...rest, use_primary_mpesa: String(rest.mpesa_integration_id || 'platform') === 'platform' }, { headers });
      setShowEdit(null);
      resetForm();
      fetchTenants();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update tenant');
    } finally { setProcessing(false); }
  };

  const handleReactivate = async (tenant) => {
    if (!window.confirm(`Reactivate ${tenant.name}? Its site (${tenant.subdomain}.smartwater.co.ke) will take payments again.`)) return;
    try {
      await axios.post(`/api/tenants/${tenant.id}/reactivate`, {}, { headers });
      fetchTenants();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reactivate tenant');
    }
  };

  const handleDeactivate = async (id) => {
    if (!window.confirm('Deactivate this tenant? Its site will stop taking payments until you reactivate it.')) return;
    try {
      await axios.delete(`/api/tenants/${id}`, { headers });
      fetchTenants();
    } catch (err) {
      setError('Failed to deactivate tenant');
    }
  };

  const openEdit = (tenant) => {
    setForm({ ...tenant, user_ids: (tenant.users || []).map(u => u.id), account_ids: (tenant.accounts || []).map(a => a.id), add_new_user: false,
      mpesa_integration_id: tenant.use_primary_mpesa || !tenant.mpesa_integration ? 'platform' : String(tenant.mpesa_integration.id) });
    loadMpesaOptions(tenant.id);
    setShowEdit(tenant);
  };

  //const inputStyle = { width: "100%", boxSizing: "border-box", background: "rgba(255,255,255,0.05)", border: "1.5px solid rgba(56,189,248,0.25)", borderRadius: 10, padding: "10px 14px", color: "#e2e8f0", fontSize: 15, outline: "none", fontFamily: "inherit" };
  //const labelStyle = { display: "block", color: "#e2e8f0", fontSize: 12.5, letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 6, fontWeight: 600 };
  //const sectionStyle = { borderBottom: "1px solid rgba(56,189,248,0.1)", paddingBottom: 16, marginBottom: 16 };

 

  return (
    <div style={{ minHeight: "100vh" }}>
      <Navbar user={user} />
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>

      <div className="mw-page" style={{ padding: "32px 24px", maxWidth: 1200, margin: "0 auto" }}>

        {/* Header */}
        <div className="mw-page-head" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 32 }}>
          <div>
            <h1 style={{ color: "#0f172a", fontSize: 26, fontWeight: 700, margin: 0 }}>Tenants</h1>
            <p style={{ color: "#475569", fontSize: 14, marginTop: 6 }}>Manage subdomains and payment configurations</p>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
          <button data-tour="platform-payments" onClick={() => setIntegrationsFor(null)}
            style={{ background: "#15803d", border: "1px solid #15803d", borderRadius: 10, padding: "10px 16px", color: "#ffffff", fontSize: 14.5, fontWeight: 600, cursor: "pointer" }}>
            Platform payments
          </button>
          <button data-tour="new-tenant" onClick={() => { resetForm(); loadMpesaOptions(null); setShowCreate(true); }}
            style={{ background: "linear-gradient(135deg, #0891b2, #0e7490)", border: "none", borderRadius: 10, padding: "10px 20px", color: "#fff", fontSize: 14.5, fontWeight: 600, cursor: "pointer" }}>
            + New Tenant
          </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div style={{ background: "rgba(248,113,113,0.1)", border: "1px solid rgba(248,113,113,0.3)", borderRadius: 10, padding: "14px 18px", color: "#f87171", marginBottom: 24 }}>
            {error}
            <button onClick={() => setError('')} style={{ float: "right", background: "none", border: "none", color: "#f87171", cursor: "pointer" }}>✕</button>
          </div>
        )}

        {/* Loading */}
        {loading ? (
          <div style={{ textAlign: "center", padding: "60px 0" }}>
            <div style={{ width: 40, height: 40, borderRadius: "50%", border: "3px solid rgba(56,189,248,0.15)", borderTop: "3px solid #38bdf8", margin: "0 auto 16px", animation: "spin 1s linear infinite" }} />
            <p style={{ color: "#475569" }}>Loading tenants...</p>
          </div>
        ) : tenants.length === 0 ? (
          <div style={{ background: "rgba(15,23,42,0.92)", borderRadius: 16, border: "1px solid rgba(56,189,248,0.15)", padding: "60px 24px", textAlign: "center" }}>
            <p style={{ color: "#cbd5e1", fontSize: 15 }}>No tenants yet</p>
            <button onClick={() => { resetForm(); loadMpesaOptions(null); setShowCreate(true); }} style={{ marginTop: 16, background: "rgba(56,189,248,0.1)", border: "1px solid rgba(56,189,248,0.25)", borderRadius: 8, padding: "8px 20px", color: "#38bdf8", fontSize: 14.5, cursor: "pointer" }}>
              Create first tenant
            </button>
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 340px), 1fr))", gap: 20, animation: "fadeIn 0.5s ease" }}>
            {tenants.map(tenant => (
              <div key={tenant.id} style={{ background: "rgba(15,23,42,0.92)", borderRadius: 16, border: `1px solid ${tenant.active ? "rgba(56,189,248,0.15)" : "rgba(248,113,113,0.15)"}`, padding: 24 }}>

                {/* Tenant header */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
                  <div>
                    <h3 style={{ color: "#ffffff", fontSize: 19, fontWeight: 700, margin: 0 }}>{tenant.name}</h3>
                    <a href={`https://${tenant.subdomain}.smartwater.co.ke`} target="_blank" rel="noreferrer"
                      style={{ color: "#38bdf8", fontSize: 13.5, textDecoration: "none" }}>
                      {tenant.subdomain}.smartwater.co.ke ↗
                    </a>
                  </div>
                  <span style={{ background: tenant.active ? "rgba(16,185,129,0.1)" : "rgba(248,113,113,0.1)", border: `1px solid ${tenant.active ? "rgba(16,185,129,0.3)" : "rgba(248,113,113,0.3)"}`, borderRadius: 6, padding: "3px 10px", color: tenant.active ? "#10b981" : "#f87171", fontSize: 12.5, fontWeight: 600 }}>
                    {tenant.active ? 'Active' : 'Inactive'}
                  </span>
                </div>

                {/* Stats */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
                  {[
                    ['Meters', tenant.meter_count],
                    ['Transactions', tenant.transaction_count],
                    ['Total Transacted', `KES ${Number(tenant.total_transacted).toLocaleString()}`],
                    ['Commission Earned', `KES ${Number(tenant.total_commission).toLocaleString()}`],
                  ].map(([k, v]) => (
                    <div key={k} style={{ background: "rgba(255,255,255,0.03)", borderRadius: 8, padding: "10px 12px" }}>
                      <div style={{ color: "#cbd5e1", fontSize: 12, letterSpacing: 1, textTransform: "uppercase", marginBottom: 4 }}>{k}</div>
                      <div style={{ color: k.includes('KES') ? "#10b981" : "#e2e8f0", fontWeight: 700, fontSize: 15 }}>{v}</div>
                    </div>
                  ))}
                </div>

                {/* Payment methods */}
                <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
                  {tenant.mpesa_enabled && (
                    <span style={{ background: "rgba(0,168,56,0.1)", border: "1px solid rgba(0,168,56,0.3)", borderRadius: 6, padding: "3px 10px", color: "#00a838", fontSize: 12.5, fontWeight: 600 }}>
                      {tenant.use_primary_mpesa || !tenant.mpesa_integration
                        ? 'M-Pesa · shared platform paybill'
                        : `M-Pesa · ${tenant.mpesa_integration.shortcode_type === 'till' ? 'Till' : 'Paybill'} ${tenant.mpesa_integration.till_number || tenant.mpesa_integration.shortcode}`}
                    </span>
                  )}
                  {tenant.kcb_enabled && (
                    <span style={{ background: "rgba(59,130,246,0.1)", border: "1px solid rgba(59,130,246,0.3)", borderRadius: 6, padding: "3px 10px", color: "#3b82f6", fontSize: 12.5, fontWeight: 600 }}>
                      KCB
                    </span>
                  )}
                  {tenant.airtel_enabled && (
                    <span style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 6, padding: "3px 10px", color: "#ef4444", fontSize: 12.5, fontWeight: 600 }}>
                      Airtel
                    </span>
                  )}
                </div>

                {/* Commission rate */}
                <div style={{ color: "#cbd5e1", fontSize: 13.5, marginBottom: 16 }}>
                  Commission rate: <span style={{ color: "#38bdf8", fontWeight: 600 }}>{tenant.commission_rate}%</span>
                  {tenant.address && ` · ${tenant.address}`}
                </div>

                {/* Accounts (decide which meters pay into this tenant's paybill) */}
                <div style={{ fontSize: 13.5, marginBottom: 8, color: "#e2e8f0" }}>
                  <span style={{ color: "#cbd5e1" }}>Accounts: </span>
                  {(tenant.accounts || []).length
                    ? tenant.accounts.map(a => a.name).join(', ')
                    : <span style={{ color: "#fbbf24" }}>None — no meters can be paid for on this site yet</span>}
                </div>

                {/* Users */}
                <div style={{ fontSize: 13.5, marginBottom: 16, color: "#e2e8f0" }}>
                  {(tenant.users || []).length
                    ? tenant.users.map(u => u.name).join(', ')
                    : <span style={{ color: "#fbbf24" }}>No users assigned — edit to add one</span>}
                </div>

                {/* Actions */}
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={() => openEdit(tenant)} style={{ flex: 1, background: "rgba(56,189,248,0.1)", border: "1px solid rgba(56,189,248,0.25)", borderRadius: 8, padding: "8px", color: "#38bdf8", fontSize: 13.5, fontWeight: 600, cursor: "pointer" }}>
                    Edit
                  </button>
                  <button data-tour="tenant-payments" onClick={() => setIntegrationsFor(tenant)} style={{ flex: 1, background: "rgba(0,168,56,0.1)", border: "1px solid rgba(0,168,56,0.3)", borderRadius: 8, padding: "8px", color: "#00a838", fontSize: 13.5, fontWeight: 600, cursor: "pointer" }}>
                    Payments ({tenant.integration_count || 0})
                  </button>
                  {tenant.active && (
                    <button onClick={() => handleDeactivate(tenant.id)} style={{ flex: 1, background: "rgba(248,113,113,0.1)", border: "1px solid rgba(248,113,113,0.25)", borderRadius: 8, padding: "8px", color: "#f87171", fontSize: 13.5, fontWeight: 600, cursor: "pointer" }}>
                      Deactivate
                    </button>
                  )}
                  {!tenant.active && (
                    <button onClick={() => handleReactivate(tenant)} style={{ flex: 1, background: "#047857", border: "1px solid #047857", borderRadius: 8, padding: "8px", color: "#ffffff", fontSize: 13.5, fontWeight: 600, cursor: "pointer" }}>
                      Reactivate
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div className="mw-modal-overlay" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: 20 }}>
          <div className="mw-modal" style={{ background: "#0f172a", borderRadius: 16, border: "1px solid rgba(56,189,248,0.2)", padding: 32, width: "100%", maxWidth: 500, animation: "fadeIn 0.3s ease" }}>
            <h3 style={{ color: "#ffffff", fontSize: 20, fontWeight: 700, margin: "0 0 24px" }}>Create New Tenant</h3>
            <TenantForm form={form} setForm={setForm} users={users} accounts={accounts} mpesa={mpesaOptions} processing={processing} showEdit={showEdit} onSubmit={handleCreate} onCancel={() => { setShowCreate(false); resetForm(); }} submitLabel="Register Tenant" />
          </div>
        </div>
      )}

      {/* Payment integrations */}
      {integrationsFor !== undefined && (
        <IntegrationsModal
          tenant={integrationsFor}
          headers={headers}
          onClose={() => { setIntegrationsFor(undefined); fetchTenants(); }}
        />
      )}

      {/* Edit Modal */}
      {showEdit && (
        <div className="mw-modal-overlay" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: 20 }}>
          <div className="mw-modal" style={{ background: "#0f172a", borderRadius: 16, border: "1px solid rgba(56,189,248,0.2)", padding: 32, width: "100%", maxWidth: 500, animation: "fadeIn 0.3s ease" }}>
            <h3 style={{ color: "#ffffff", fontSize: 20, fontWeight: 700, margin: "0 0 24px" }}>Edit Tenant — {showEdit.name}</h3>
            <TenantForm form={form} setForm={setForm} users={users} accounts={accounts} mpesa={mpesaOptions} processing={processing} showEdit={showEdit} onSubmit={handleUpdate} onCancel={() => { setShowEdit(null); resetForm(); }} submitLabel="Save Changes" />
          </div>
        </div>
      )}
    </div>
  );
}